import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  CUSTOM_ELEMENTS_SCHEMA,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';

import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';

import { AppSharedModule } from 'app/app-shared.module';
import { Field } from 'app/providers/storage-api/field.service';
import { CustomFieldsService } from '../custom-fields.service';

@Component({
  selector: 'app-field-alias-selector',
  templateUrl: './field-alias-selector.component.html',
  styleUrls: ['./field-alias-selector.component.scss'],
  imports: [AppSharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class FieldAliasSelectorComponent implements OnInit, OnChanges, OnDestroy {
  constructor(private cfService: CustomFieldsService) {}
  @Input() subType: any;
  @Input() fields: Field[];
  @Input() selectedAliasedFields?: string[];
  @Input() newAlias: boolean;
  @Input() isReadOnly: boolean;

  @Output() selectionChanged = new EventEmitter();

  includFieldColumns = ['include', 'fieldName'];
  includedFieldsDataSource: MatTableDataSource<Field>;
  filterFieldsFn = (subType: string) =>
    this.fields.filter((f) => f.fieldType === subType && !f.alias);
  private filteredFields: Field[];
  includedFieldsFG: FormGroup;
  includedFieldsSub$: Subscription;
  search$ = new Subject<string>();

  ngOnInit() {
    this.includedFieldsDataSource.filterPredicate = (data, filter: string) => {
      return data.label.toLowerCase().includes(filter.toLowerCase());
    };
    this.search$.pipe(debounceTime(200), distinctUntilChanged()).subscribe((val) => {
      this.includedFieldsDataSource.filter = val;
    });

    this.resetIncludedFields();
  }

  ngOnChanges(changes: SimpleChanges) {
    // Initially for both modes, refresh for edit mode, or when subtype changes in new mode
    if (changes.subType && changes.subType.currentValue) {
      this.includedFieldsFG = new FormGroup({});
      this.filteredFields = this.filterFieldsFn(this.subType);

      //If new mode (field-create)
      if (this.newAlias) {
        for (const f of this.filteredFields) {
          this.includedFieldsFG.addControl(f.fieldName, new FormControl(false));
        }
        //If edit mode (field-view)
      } else {
        this.includedFieldsFG.reset();
        for (const f of this.filteredFields) {
          const isFieldAliased = this.selectedAliasedFields.some((af) => af === f.fieldName);
          const fieldControl = new FormControl(isFieldAliased);
          if (this.isReadOnly) {
            fieldControl.disable();
          }
          this.includedFieldsFG.addControl(f.fieldName, fieldControl);
        }

        this.setupIncludedFields();
      }
      this.includedFieldsDataSource = new MatTableDataSource(this.filteredFields);
    }
    if (changes.isReadOnly) {
      this.isReadOnly ? this.includedFieldsFG.disable() : this.includedFieldsFG.enable();
    }

    this.includedFieldsSub$?.unsubscribe();
    if (changes.selectedAliasedFields) {
      this.resetIncludedFields();
    }
    this.includedFieldsSub$ = this.includedFieldsFG.valueChanges.subscribe(() => {
      const includedFields = this.setupIncludedFields();

      const containsPII = this.cfService.anyFieldContainsValue(
        includedFields,
        this.filteredFields,
        'redacted'
      );
      this.selectionChanged.emit({
        includedFields,
        filteredFields: this.filteredFields,
        containsPII,
      });
    });
  }

  ngOnDestroy(): void {
    this.includedFieldsSub$?.unsubscribe();
    this.search$.unsubscribe();
  }

  private disableLastChecked(includedFields: string[]) {
    if (this.isReadOnly) {
      return;
    }

    for (const field of includedFields) {
      this.includedFieldsFG.controls[field].enable({ emitEvent: false });
    }

    if (includedFields.length === 1) {
      this.includedFieldsFG.controls[includedFields[0]].disable({ emitEvent: false });
    }
  }

  private resetIncludedFields() {
    if (!this.selectedAliasedFields) {
      return;
    }

    this.includedFieldsFG.reset();
    if (!this.isReadOnly) {
      this.includedFieldsFG.enable();
    }

    this.includedFieldsFG.patchValue(
      (this.selectedAliasedFields as string[]).reduce((obj, f) => (obj = { ...obj, [f]: true }), {})
    );

    this.setupIncludedFields();
  }

  private setupIncludedFields(): string[] {
    const includedFields = Object.keys(this.includedFieldsFG.controls).filter(
      (k) => this.includedFieldsFG.controls[k].value
    );

    if (!this.newAlias) {
      this.disableLastChecked(includedFields);
    }

    return includedFields;
  }

  onFilterFields(text: string) {
    const filterVal = text ? text.trim() : '';
    this.search$.next(filterVal);
  }
}
